/**
 * React hook for multi-row assessment drafts (session batch save).
 * @see frontend/src/lib/session-assessment-draft.ts
 */

import { useCallback, useRef, useState } from 'react';
import type { BatchAssessmentUpdate } from '../lib/assessment-versions';
import {
  applyDraftPatch,
  buildBatchUpdates,
  clearDraftState,
  createEmptyDirtySet,
  createEmptyDraftMap,
  draftFromRow,
  getEffectiveRow,
  isDirtySet,
  rowDiffersFromBaseline,
  type AssessmentRowDraft,
  type SessionSaveContext,
  type DraftMap,
  type SessionAssessmentRow,
} from '../lib/session-assessment-draft';

export interface UseSessionAssessmentDraftResult<T extends SessionAssessmentRow> {
  /** Merge baseline + drafts for the given row (UI binding). */
  getEffectiveRow: (row: T) => T;
  /** Update one or more fields on a row; marks the row dirty. */
  updateRow: (assessmentId: string, patch: AssessmentRowDraft) => void;
  /** Copy full row into draft (call before switching competency/criterion). */
  flushRow: (row: T) => void;
  /** Mark evidence as edited for batch inclusion. */
  touchEvidence: (assessmentId: string, evidence: unknown) => void;
  /** Current draft overrides, for pre-flight validation before a batch save. */
  drafts: DraftMap;
  isDirty: () => boolean;
  dirtyCount: number;
  dirtyIds: ReadonlySet<string>;
  /**
   * Batch payload for the dirty rows. The save context carries the session's
   * suggested development level (G3-14b) and the authenticated instructor and
   * the save instant that both audit pairs are stamped with (G6-04); omit it
   * and no prefill is written and neither pair is stamped.
   */
  buildBatchUpdates: (save?: SessionSaveContext) => BatchAssessmentUpdate[];
  discard: () => void;
  /** Replace baseline rows and clear drafts (after load or restore). */
  resetFromBaseline: (rows: T[]) => void;
  rowIsDirty: (assessmentId: string) => boolean;
  rowDiffersFromBaseline: (row: T) => boolean;
}

export function useSessionAssessmentDraft<T extends SessionAssessmentRow>(): UseSessionAssessmentDraftResult<T> {
  const baselineRef = useRef<T[]>([]);
  const [drafts, setDrafts] = useState<DraftMap>(createEmptyDraftMap);
  const dirtyIdsRef = useRef<Set<string>>(createEmptyDirtySet());
  const [dirtyCount, setDirtyCount] = useState(0);

  const syncDirtyCount = useCallback(() => {
    setDirtyCount(dirtyIdsRef.current.size);
  }, []);

  const markDirty = useCallback(
    (assessmentId: string) => {
      if (!dirtyIdsRef.current.has(assessmentId)) {
        dirtyIdsRef.current.add(assessmentId);
        syncDirtyCount();
      }
    },
    [syncDirtyCount],
  );

  const updateRow = useCallback(
    (assessmentId: string, patch: AssessmentRowDraft) => {
      markDirty(assessmentId);
      setDrafts((prev) => applyDraftPatch(prev, assessmentId, patch));
    },
    [markDirty],
  );

  const flushRow = useCallback(
    (row: T) => {
      markDirty(row.id);
      setDrafts((prev) => applyDraftPatch(prev, row.id, draftFromRow(row)));
    },
    [markDirty],
  );

  const touchEvidence = useCallback(
    (assessmentId: string, evidence: unknown) => {
      updateRow(assessmentId, { evidence, evidenceTouched: true });
    },
    [updateRow],
  );

  const discard = useCallback(() => {
    const cleared = clearDraftState();
    dirtyIdsRef.current = cleared.dirtyIds;
    setDrafts(cleared.drafts);
    syncDirtyCount();
  }, [syncDirtyCount]);

  const resetFromBaseline = useCallback(
    (rows: T[]) => {
      baselineRef.current = rows;
      discard();
    },
    [discard],
  );

  const getEffectiveRowBound = useCallback(
    (row: T) => getEffectiveRow(row, drafts),
    [drafts],
  );

  const buildBatchUpdatesBound = useCallback(
    (save?: SessionSaveContext) =>
      buildBatchUpdates(baselineRef.current, drafts, dirtyIdsRef.current, save),
    [drafts, dirtyCount],
  );

  const isDirty = useCallback(() => isDirtySet(dirtyIdsRef.current), [dirtyCount]);

  const rowIsDirty = useCallback(
    (assessmentId: string) => dirtyIdsRef.current.has(assessmentId),
    [dirtyCount],
  );

  const rowDiffersFromBaselineBound = useCallback(
    (row: T) => rowDiffersFromBaseline(row, drafts[row.id]),
    [drafts],
  );

  return {
    drafts,
    getEffectiveRow: getEffectiveRowBound,
    updateRow,
    flushRow,
    touchEvidence,
    isDirty,
    dirtyCount,
    dirtyIds: dirtyIdsRef.current,
    buildBatchUpdates: buildBatchUpdatesBound,
    discard,
    resetFromBaseline,
    rowIsDirty,
    rowDiffersFromBaseline: rowDiffersFromBaselineBound,
  };
}
